import { useMemo, useState } from 'react';
import {
  MetamodelContextSettings,
  MetamodelReferenceCompletenessContext,
} from 'metamodel/types';
import { APIFieldAttributes, Metamodel } from '@ardoq/api-types';
import { BreadCrumbNavigation } from 'metamodel/metamodelStats/breadCrumbNavigation/BreadCrumbNavigation';
import { GremlinSearchTableWithLoader as GremlinSearchTableWithLoaderBase } from '../GremlinSearchTableWithLoader';
import { DisplayPreference } from './types';
import {
  getDefaultDisplayPreference,
  getReferenceCompletionQuery,
} from './utils';
import {
  ReferenceCompletionExplanation,
  ReferenceCompletionTitle,
} from './atoms';
import { GroupFieldLayout, RadioGroup } from '@ardoq/forms';
import { Box } from '@ardoq/layout';
import { t, type I18nLocale } from '@ardoq/i18n';

type ReferenceCompletionStatsProps = {
  context: MetamodelReferenceCompletenessContext & MetamodelContextSettings;
  metamodel: Metamodel;
  nonTemplateFieldsByName: Map<string, APIFieldAttributes>;
  i18nLocale: I18nLocale;
};
const ReferenceCompletionStats = ({
  context,
  metamodel,
  nonTemplateFieldsByName,
  i18nLocale,
}: ReferenceCompletionStatsProps) => {
  const [displayPreference, setDisplayPreference] = useState<DisplayPreference>(
    getDefaultDisplayPreference(context)
  );

  const displayPreferenceOptions = useMemo(
    () => [
      {
        value: DisplayPreference.WITH_REFERENCE,
        label: t(
          i18nLocale,
          'metamodel:stats.referenceCompletion.displayPreference.withReference',
          'With reference'
        ),
      },
      {
        value: DisplayPreference.WITHOUT_REFERENCE,
        label: t(
          i18nLocale,
          'metamodel:stats.referenceCompletion.displayPreference.withoutReference',
          'Without reference'
        ),
      },
    ],
    [i18nLocale]
  );

  const query = getReferenceCompletionQuery(
    metamodel,
    context,
    displayPreference
  );
  return (
    <Box position="relative">
      <BreadCrumbNavigation
        metamodel={metamodel}
        context={context}
        nonTemplateFieldsByName={nonTemplateFieldsByName}
      />
      <Box marginTop="small">
        <ReferenceCompletionTitle
          metamodel={metamodel}
          context={context}
          i18nLocale={i18nLocale}
        />
      </Box>

      <Box display="inline-block">
        <RadioGroup
          value={displayPreference}
          options={displayPreferenceOptions}
          onValueChange={value =>
            setDisplayPreference(value as DisplayPreference)
          }
          groupFieldLayout={GroupFieldLayout.HORIZONTAL}
        />
      </Box>
      <ReferenceCompletionExplanation
        metamodel={metamodel}
        context={context}
        displayPreference={displayPreference}
      />
      <GremlinSearchTableWithLoaderBase query={query} />
    </Box>
  );
};

export { ReferenceCompletionStats };
